import { StyledSelect } from "@ando/ui";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2, Settings, UserCheck, UserCog, UserMinus } from "lucide-react";
import * as React from "react";

import type { AdminOrganization, AdminOrganizationMember, OrgRole } from "@ando/core";
import { Avatar, AvatarFallback, Badge, Button, Checkbox, cn, useToast } from "@ando/ui";

import { UserDetailDrawer } from "@/features/admin/components/UserDetailDrawer";
import { initialsOf } from "@/lib/initials";
import { ROLE_ICON } from "@/lib/roles";

import {
  useAdminOrg,
  useAdminOrgMembers,
  useReactivateAdminOrgMember,
  useRemoveAdminOrgMember,
  useUpdateAdminOrgMemberRole,
} from "@/features/admin/hooks/useAdminOrg";

import { MemberSettingsDialog } from "./MemberSettingsDialog";
import { MigrateUserDialog } from "./MigrateUserDialog";

import {
  ConfirmDialog,
  LoadingRow,
  PaginationControls,
  SearchInput,
  type SortDir,
  SortHeader,
  usePagination,
} from "./_shared";

/**
 * Members tab — sortable table, search, pagination, bulk-suspend,
 * row click → UserDetailDrawer placeholder (Stream 6).
 *
 * v1 reference: ``OrgMembersTab.tsx`` (288 LoC). Mandatory upgrades vs
 * v1: sortable column headers, debounced search, paginated rows (25/50/
 * 100), checkbox bulk-bar, destructive Suspend confirm + undo toast.
 */

export interface OrgMembersTabProps {
  orgId: string;
}

function compareMembers(
  a: AdminOrganizationMember,
  b: AdminOrganizationMember,
  col: string,
  dir: SortDir,
): number {
  if (dir === null) return 0;
  const sign = dir === "asc" ? 1 : -1;
  switch (col) {
    case "name": {
      const aV = (a.name ?? a.email ?? "").toLowerCase();
      const bV = (b.name ?? b.email ?? "").toLowerCase();
      return aV.localeCompare(bV) * sign;
    }
    case "role":
      return a.role.localeCompare(b.role) * sign;
    case "joined": {
      const aT = a.joined_at ? Date.parse(a.joined_at) : 0;
      const bT = b.joined_at ? Date.parse(b.joined_at) : 0;
      return (aT - bT) * sign;
    }
    case "status": {
      // removed (0) < pending invite (1) < active (2)
      const rank = (m: AdminOrganizationMember): number => (!m.is_active ? 0 : m.joined_at ? 2 : 1);
      return (rank(a) - rank(b)) * sign;
    }
    default:
      return 0;
  }
}

export function OrgMembersTab({ orgId }: OrgMembersTabProps): React.ReactElement {
  const { toast } = useToast();
  const { i18n } = useLingui();
  const orgQuery = useAdminOrg(orgId);
  const membersQuery = useAdminOrgMembers(orgId);
  const updateRole = useUpdateAdminOrgMemberRole(orgId);
  const removeMember = useRemoveAdminOrgMember(orgId);
  const reactivateMember = useReactivateAdminOrgMember(orgId);

  const [search, setSearch] = React.useState("");
  const [sortCol, setSortCol] = React.useState<string | null>("name");
  const [sortDir, setSortDir] = React.useState<SortDir>("asc");
  const { page, setPage, pageSize, setPageSize } = usePagination(25);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [confirmRemove, setConfirmRemove] = React.useState<AdminOrganizationMember | null>(null);
  const [bulkConfirm, setBulkConfirm] = React.useState<"demote" | "delete" | null>(null);
  // Drill-down state — opens the cross-cutting ``UserDetailDrawer`` for a
  // single member. Closing the drawer (backdrop / ESC / onOpenChange) sets
  // back to ``null`` so the table regains focus.
  const [drillDownUserId, setDrillDownUserId] = React.useState<string | null>(null);
  // Per-member Settings dialog (limits override + connector access).
  const [settingsMember, setSettingsMember] = React.useState<AdminOrganizationMember | null>(null);
  // Migrate-user CTA (real dialog comes from P1). Storing the full
  // member object — instead of just a userId — lets the trigger render
  // the human-readable label inside MigrateUserDialog's confirm copy
  // without an extra lookup against ``membersQuery.data``.
  const [migrateTarget, setMigrateTarget] = React.useState<AdminOrganizationMember | null>(null);

  const allMembers = membersQuery.data ?? [];
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allMembers;
    return allMembers.filter(
      (m) => (m.email ?? "").toLowerCase().includes(q) || (m.name ?? "").toLowerCase().includes(q),
    );
  }, [allMembers, search]);

  const sorted = React.useMemo(() => {
    if (sortDir === null || sortCol === null) return filtered;
    return [...filtered].sort((a, b) => compareMembers(a, b, sortCol, sortDir));
  }, [filtered, sortCol, sortDir]);

  const paged = React.useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const allOnPageSelected = paged.length > 0 && paged.every((m) => selected.has(m.user_id));

  const togglePageSelection = (): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        paged.forEach((m) => next.delete(m.user_id));
      } else {
        paged.forEach((m) => next.add(m.user_id));
      }
      return next;
    });
  };

  const handleSort = (col: string, dir: SortDir): void => {
    setSortCol(dir === null ? null : col);
    setSortDir(dir);
  };

  const handleRoleChange = (member: AdminOrganizationMember, role: OrgRole): void => {
    const previous = member.role;
    updateRole.mutate(
      { userId: member.user_id, role },
      {
        onSuccess: () => {
          toast.success(i18n._(msg`Ruolo aggiornato`), {
            description: `${member.email ?? member.user_id} → ${role}`,
            action: {
              label: i18n._(msg`Annulla`),
              onClick: () => updateRole.mutate({ userId: member.user_id, role: previous }),
            },
            duration: 5000,
          });
        },
        onError: (err) => {
          toast.error(i18n._(msg`Aggiornamento ruolo fallito`), {
            description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          });
        },
      },
    );
  };

  const handleRemoveConfirmed = (): void => {
    if (!confirmRemove) return;
    const m = confirmRemove;
    removeMember.mutate(m.user_id, {
      onSuccess: () => {
        toast.success(i18n._(msg`Membro rimosso`), {
          description: i18n._(msg`${m.email ?? m.user_id} non è più membro.`),
          duration: 5000,
        });
        setConfirmRemove(null);
      },
      onError: (err) => {
        toast.error(i18n._(msg`Rimozione fallita`), {
          description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        });
      },
    });
  };

  const handleReactivate = (member: AdminOrganizationMember): void => {
    reactivateMember.mutate(member.user_id, {
      onSuccess: () => {
        toast.success(i18n._(msg`Membro riattivato`), {
          description: i18n._(msg`${member.email ?? member.user_id} è di nuovo membro.`),
          duration: 5000,
        });
      },
      onError: (err) => {
        // Single-org guard (409) → the user joined another org meanwhile;
        // point the admin at the migrate flow rather than a raw error.
        const raw = err instanceof Error ? err.message : "";
        toast.error(i18n._(msg`Riattivazione fallita`), {
          description: raw.includes("user_in_other_org")
            ? i18n._(msg`L'utente è ora in un'altra organizzazione. Usa "Migra" per spostarlo.`)
            : raw || i18n._(msg`Errore inatteso`),
        });
      },
    });
  };

  const handleBulkConfirmed = (): void => {
    if (!bulkConfirm) return;
    const action = bulkConfirm;
    const ids = Array.from(selected);
    // Bulk wired client-side via N parallel mutations — BE has no
    // dedicated bulk endpoint. ``Promise.allSettled`` so partial
    // failures surface a count instead of bailing on the first.
    Promise.allSettled(
      ids.map((id) =>
        action === "delete"
          ? removeMember.mutateAsync(id)
          : updateRole.mutateAsync({ userId: id, role: "member" }),
      ),
    ).then((results) => {
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast.success(
          action === "delete" ? i18n._(msg`Membri rimossi`) : i18n._(msg`Ruolo impostato a Membro`),
          {
            description: i18n._(msg`${ids.length} operazioni completate.`),
          },
        );
      } else {
        toast.error(i18n._(msg`Operazione parziale`), {
          description: i18n._(msg`${ids.length - failed} successi, ${failed} errori.`),
        });
      }
      setSelected(new Set());
      setBulkConfirm(null);
    });
  };

  const handleRowDrillDown = (_member: AdminOrganizationMember): void => {
    // Hidden until Stream 6 wires the real UserDetailDrawer: the drawer is still
    // a placeholder (user={null}), so drilling in would open an empty panel.
    // Re-enable by restoring: setDrillDownUserId(member.user_id).
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={i18n._(msg`Cerca membro`)}
          placeholder={i18n._(msg`Cerca per nome o email…`)}
        />
        <div className="text-[12px] text-ink-3">
          {i18n._(
            plural(allMembers.length, {
              one: "# membro totali",
              other: "# membri totali",
            }),
          )}
        </div>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-accent/40 bg-transparent px-3 py-2 text-[12.5px] text-ink-1"
          role="region"
          aria-label={i18n._(msg`Azioni di gruppo`)}
        >
          <span>
            <Trans>
              <strong className="font-semibold">{selected.size}</strong> selezionati
            </Trans>
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBulkConfirm("demote")}
            >
              <Trans>Imposta come Membro</Trans>
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setBulkConfirm("delete")}
            >
              <Trans>Elimina</Trans>
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              <Trans>Annulla selezione</Trans>
            </Button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-hairline bg-card/55">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-hairline border-b text-left">
              <th scope="col" className="w-10 px-3 py-2">
                <Checkbox
                  aria-label={i18n._(msg`Seleziona tutto`)}
                  checked={allOnPageSelected}
                  onChange={togglePageSelection}
                />
              </th>
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Membro`)}
                  columnId="name"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Ruolo`)}
                  columnId="role"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="hidden px-3 py-2 md:table-cell">
                <SortHeader
                  label={i18n._(msg`Iscritto`)}
                  columnId="joined"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              {/* invited_at — relative-time column, parallel to "Iscritto"
                  for visibility of in-flight invitations. Sortable would
                  duplicate the joined sort for accepted members, so we
                  leave it un-sortable (heading is a plain label). */}
              <th
                scope="col"
                className="hidden px-3 py-2 lg:table-cell"
                title={i18n._(msg`Data di invito (membro non ancora attivo)`)}
              >
                <span className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                  {i18n._(msg`Invitato`)}
                </span>
              </th>
              {/* tier — shows the member-level override ("override ×N")
                  when set, else inherits the org tier ("org ×N", subtle).
                  Independent column rather than a sub-row to keep the
                  table scannable. */}
              <th scope="col" className="hidden px-3 py-2 lg:table-cell">
                <span className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                  {i18n._(msg`Tier`)}
                </span>
              </th>
              <th scope="col" className="hidden px-3 py-2 lg:table-cell">
                <SortHeader
                  label={i18n._(msg`Stato`)}
                  columnId="status"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                <span className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                  <Trans>Azioni</Trans>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {membersQuery.isLoading ? (
              <tr>
                <td colSpan={8} className="px-3">
                  <LoadingRow label={i18n._(msg`Caricamento membri…`)} />
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-[12px] text-ink-3">
                  {search ? i18n._(msg`Nessun risultato`) : i18n._(msg`Nessun membro`)}
                </td>
              </tr>
            ) : (
              paged.map((m) => {
                const isSel = selected.has(m.user_id);
                const RoleGlyph = ROLE_ICON[m.role];
                return (
                  <tr
                    key={m.user_id}
                    className={cn(
                      "border-hairline/60 border-b transition-colors last:border-b-0",
                      isSel ? "bg-ando-orange-soft/30" : "hover:bg-ando-orange-soft/20",
                    )}
                  >
                    <td className="h-8 px-3 py-2 align-middle">
                      <Checkbox
                        aria-label={i18n._(msg`Seleziona ${m.email ?? m.user_id}`)}
                        checked={isSel}
                        onChange={(e) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(m.user_id);
                            else next.delete(m.user_id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td className="h-8 px-3 py-2 align-middle">
                      <button
                        type="button"
                        onClick={() => handleRowDrillDown(m)}
                        className="inline-flex max-w-full items-center gap-2 truncate text-left text-ink-1 transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        aria-label={i18n._(msg`Apri dettagli di ${m.email ?? m.user_id}`)}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{initialsOf(m.name, m.email)}</AvatarFallback>
                        </Avatar>
                        <span className="min-w-0 truncate">
                          <span className="block truncate font-medium">
                            {m.name ?? m.email ?? m.user_id}
                          </span>
                          {m.name && m.email ? (
                            <span className="block truncate text-[11px] text-ink-3">{m.email}</span>
                          ) : null}
                          {m.product_access && (
                            <span className="block text-[11px] text-ink-3">
                              {m.product_access === "both"
                                ? i18n._(msg`Workspace ed Engine`)
                                : m.product_access === "engine"
                                  ? i18n._(msg`Solo Engine`)
                                  : i18n._(msg`Solo Workspace`)}
                            </span>
                          )}
                        </span>
                      </button>
                    </td>
                    <td className="h-8 px-3 py-2 align-middle">
                      <div className="inline-flex items-center gap-1.5">
                        <RoleGlyph className="size-3.5" aria-hidden />
                        <StyledSelect
                          value={m.role}
                          onChange={(e) => handleRoleChange(m, e.target.value as OrgRole)}
                          aria-label={i18n._(msg`Ruolo di ${m.email ?? m.user_id}`)}
                          className="h-7 rounded border border-hairline bg-card px-1.5 text-[12px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          disabled={updateRole.isPending}
                        >
                          <option value="owner">{i18n._(msg`Owner`)}</option>
                          <option value="admin">{i18n._(msg`Admin`)}</option>
                          <option value="member">{i18n._(msg`Member`)}</option>
                        </StyledSelect>
                      </div>
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle text-ink-3 md:table-cell">
                      {m.joined_at ? new Date(m.joined_at).toLocaleDateString(i18n.locale) : "—"}
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle text-ink-3 lg:table-cell">
                      {m.invited_at
                        ? new Date(m.invited_at).toLocaleDateString(i18n.locale, {
                            day: "2-digit",
                            month: "short",
                          })
                        : "—"}
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle lg:table-cell">
                      {/* tier badge: override > inherited org tier. Two
                          variants keep the visual hierarchy honest — the
                          override badge is solid (catches the eye) while
                          the inherited fallback is a soft outline so a
                          plain "org ×N" row reads as ambient context. */}
                      {m.usage_multiplier_override != null ? (
                        <Badge
                          variant="default"
                          className="text-[10px]"
                          title={i18n._(msg`Override impostato sul singolo membro`)}
                        >
                          {i18n._(msg`override ×${m.usage_multiplier_override}`)}
                        </Badge>
                      ) : (
                        <span
                          className="text-[11px] text-ink-3"
                          title={i18n._(msg`Ereditato dal tier dell'organizzazione`)}
                        >
                          {i18n._(msg`org ×${orgQuery.data?.usage_multiplier ?? "—"}`)}
                        </span>
                      )}
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle lg:table-cell">
                      {!m.is_active ? (
                        <Badge variant="destructive" className="text-[10px]">
                          <Trans>Rimosso</Trans>
                        </Badge>
                      ) : m.joined_at ? (
                        <Badge variant="success" className="text-[10px]">
                          <Trans>Attivo</Trans>
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">
                          <Trans>In attesa</Trans>
                        </Badge>
                      )}
                    </td>
                    <td className="h-8 px-3 py-2 text-right align-middle">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => setSettingsMember(m)}
                          aria-label={i18n._(msg`Impostazioni per ${m.email ?? m.user_id}`)}
                          title={i18n._(msg`Impostazioni membro`)}
                        >
                          <Settings size={14} aria-hidden />
                        </Button>
                        {/* Migrate-to-org affordance hidden until P1 wires the
                            real MigrateUserDialog (currently a no-op stub).
                            Restore a <Button onClick={() => setMigrateTarget(m)}>
                            with the ArrowRightLeft icon when it's functional. */}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleRowDrillDown(m)}
                          aria-label={i18n._(msg`Apri dettagli di ${m.email ?? m.user_id}`)}
                        >
                          <UserCog size={14} aria-hidden />
                        </Button>
                        {!m.is_active ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-success hover:bg-success/10"
                            onClick={() => handleReactivate(m)}
                            disabled={reactivateMember.isPending}
                            aria-label={i18n._(msg`Riattiva ${m.email ?? m.user_id}`)}
                            title={i18n._(msg`Riattiva membro`)}
                          >
                            {reactivateMember.isPending ? (
                              <Loader2 size={14} className="animate-spin" aria-hidden />
                            ) : (
                              <UserCheck size={14} aria-hidden />
                            )}
                          </Button>
                        ) : m.role !== "owner" ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                            onClick={() => setConfirmRemove(m)}
                            aria-label={i18n._(msg`Rimuovi ${m.email ?? m.user_id}`)}
                          >
                            <UserMinus size={14} aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <ConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemove(null);
        }}
        title={i18n._(msg`Rimuovere membro?`)}
        description={
          confirmRemove ? (
            <Trans>
              <strong>{confirmRemove.email ?? confirmRemove.user_id}</strong> perderà l&apos;accesso
              a questa organizzazione. Verrà migrato al Default Tenant; i dati personali (TIER 1+2)
              seguono l&apos;utente.
            </Trans>
          ) : null
        }
        confirmLabel={removeMember.isPending ? i18n._(msg`Rimozione…`) : i18n._(msg`Rimuovi`)}
        destructive
        loading={removeMember.isPending}
        onConfirm={handleRemoveConfirmed}
      />

      <ConfirmDialog
        open={bulkConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setBulkConfirm(null);
        }}
        title={
          bulkConfirm === "delete"
            ? i18n._(msg`Eliminare i membri selezionati?`)
            : i18n._(msg`Impostare come Membro i selezionati?`)
        }
        description={
          <Trans>
            Stai per applicare l&apos;operazione a <strong>{selected.size}</strong>{" "}
            {i18n._(plural(selected.size, { one: "membro", other: "membri" }))}. Le mutazioni
            vengono inviate in parallelo e i fallimenti vengono raccolti senza interrompere le
            altre.
          </Trans>
        }
        confirmLabel={
          removeMember.isPending || updateRole.isPending ? (
            <Loader2 size={13} className="animate-spin" aria-hidden />
          ) : bulkConfirm === "delete" ? (
            i18n._(msg`Elimina`)
          ) : (
            i18n._(msg`Imposta come Membro`)
          )
        }
        destructive={bulkConfirm === "delete"}
        loading={removeMember.isPending || updateRole.isPending}
        onConfirm={handleBulkConfirmed}
      />
      {/* Cross-cutting drill-down (Round 2 wiring): the canonical
          UserDetailDrawer ports v1's slide-in tabs Overview / Sessions /
          MFA / Traces / PII Inspect (capability-gated). Mounted outside
          the table so its portal can take focus cleanly. */}
      <UserDetailDrawer
        userId={drillDownUserId}
        user={null}
        open={drillDownUserId !== null}
        onClose={() => setDrillDownUserId(null)}
      />

      {/* Per-member Settings dialog (limits override + connector
          access). Gated on ``orgQuery.data`` so we never open without
          the org tier context the dialog uses for the "inherit" label
          and override comparisons. */}
      {orgQuery.data ? (
        <MemberSettingsDialog
          org={orgQuery.data satisfies AdminOrganization}
          member={settingsMember}
          onOpenChange={(open) => {
            if (!open) setSettingsMember(null);
          }}
        />
      ) : null}

      {/* Migrate user to another org. Real dialog comes from P1; until
          then ``MigrateUserDialog`` is a local no-op stub. */}
      <MigrateUserDialog
        userId={migrateTarget?.user_id ?? ""}
        currentOrgId={orgId}
        memberLabel={migrateTarget?.name ?? migrateTarget?.email ?? migrateTarget?.user_id ?? ""}
        open={migrateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setMigrateTarget(null);
        }}
      />
    </div>
  );
}
